import { useCallback, useEffect, useMemo, useState } from "react";
import { Copy, Pencil, Plus, Search, Trash2, X } from "lucide-react";
import { api, apiError, imgUrl, formatINR } from "../../lib/api";
import { toast } from "sonner";
import { btnDark, btnGhost, btnPrimary, inputCls, labelCls } from "./shared";
import ImageManager from "./ImageManager";
import VariantEditor from "./VariantEditor";

const emptyProduct = { name: "", category_id: "", description: "", price: "", compare_at_price: "", stock: 10, is_new: false, is_bestseller: false, is_trending: false, is_active: true, variants_text: "", tags_text: "", highlights_text: "", variant_groups: [] };

export default function ProductsTab({ categories }) {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyProduct);
  const [q, setQ] = useState("");
  const [catFilter, setCatFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selected, setSelected] = useState([]);
  const [bulkPrice, setBulkPrice] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [stagedImages, setStagedImages] = useState([]);
  const load = useCallback(() => api.get("/admin/products", { params: { limit: 200 } }).then((r) => { setProducts(r.data.items); setLoaded(true); }).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const openEdit = (p) => {
    setEditing(p);
    setStagedImages([]);
    setForm(p === "new" ? { ...emptyProduct, category_id: categories[0]?.id || "" } : {
      name: p.name, category_id: p.category_id, description: p.description, price: p.price,
      compare_at_price: p.compare_at_price || "", stock: p.stock, is_new: p.is_new,
      is_bestseller: p.is_bestseller, is_trending: p.is_trending, is_active: p.is_active,
      variants_text: (p.variants || []).join(", "), tags_text: (p.tags || []).join(", "),
      highlights_text: (p.highlights || []).join("\n"),
      variant_groups: (p.variant_groups || []).map((g) => ({ ...g, options: (g.options || []).map((o) => ({ ...o, price: o.price ?? "", stock: o.stock ?? "" })) })),
    });
  };

  const save = async (e) => {
    e.preventDefault();
    const cleanGroups = (form.variant_groups || [])
      .map((g) => ({ name: (g.name || "").trim(), options: (g.options || [])
        .filter((o) => (o.label || "").trim())
        .map((o) => ({ label: o.label.trim(), price: o.price === "" || o.price == null ? null : Number(o.price),
          stock: o.stock === "" || o.stock == null ? null : Number(o.stock), images: o.images || [],
          is_default: !!o.is_default, is_active: !!o.is_active })) }))
      .filter((g) => g.name && g.options.length);
    const body = { ...form, price: Number(form.price) || 0, compare_at_price: form.compare_at_price ? Number(form.compare_at_price) : null, stock: Number(form.stock) || 0,
      variants: (form.variants_text || "").split(",").map((s) => s.trim()).filter(Boolean),
      tags: (form.tags_text || "").split(",").map((s) => s.trim()).filter(Boolean),
      highlights: (form.highlights_text || "").split("\n").map((s) => s.trim()).filter(Boolean),
      variant_groups: cleanGroups };
    delete body.variants_text; delete body.tags_text; delete body.highlights_text;
    try {
      if (editing === "new") {
        const { data: created } = await api.post("/admin/products", body);
        if (stagedImages.length) {
          const fd = new FormData();
          stagedImages.forEach((s) => fd.append("files", s.file));
          await api.post(`/admin/products/${created.id}/images`, fd);
        }
      } else {
        await api.put(`/admin/products/${editing.id}`, body);
      }
      toast.success(editing === "new" ? "Product created" : "Product saved");
      setEditing(null);
      setStagedImages([]);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this product permanently? This cannot be undone.")) return;
    await api.delete(`/admin/products/${id}`).then(() => { toast.success("Deleted"); load(); }).catch((e) => toast.error(apiError(e)));
  };

  const duplicate = async (id) => {
    try {
      const { data } = await api.post(`/admin/products/${id}/duplicate`);
      toast.success(`Duplicated as "${data.name}" (saved as draft)`);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const filtered = useMemo(() => products.filter((p) => {
    if (catFilter && p.category_id !== catFilter) return false;
    if (statusFilter === "published" && !p.is_active) return false;
    if (statusFilter === "draft" && p.is_active) return false;
    if (q.trim() && !p.name.toLowerCase().includes(q.trim().toLowerCase())) return false;
    return true;
  }), [products, q, catFilter, statusFilter]);

  const toggleSelect = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allVisibleSelected = filtered.length > 0 && filtered.every((p) => selected.includes(p.id));

  const bulkApply = async (patch) => {
    if (!selected.length) { toast.error("Select products first"); return; }
    try {
      const { data } = await api.post("/admin/products-bulk-update", { ids: selected, ...patch });
      toast.success(`${data.updated} product(s) updated`);
      setSelected([]);
      setBulkPrice("");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div data-testid="admin-products-tab">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative min-w-[180px] flex-1">
          <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input data-testid="products-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products…" className={`${inputCls} mt-0 pl-10`} />
        </div>
        <select data-testid="products-category-filter" value={catFilter} onChange={(e) => setCatFilter(e.target.value)}
          className="rounded-xl border border-input bg-white/80 px-3 py-2.5 text-xs outline-none">
          <option value="">All categories</option>
          {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select data-testid="products-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-xl border border-input bg-white/80 px-3 py-2.5 text-xs outline-none">
          <option value="">All statuses</option>
          <option value="published">Published</option>
          <option value="draft">Drafts</option>
        </select>
        <button data-testid="add-product-btn" onClick={() => openEdit("new")} className={btnDark}><Plus size={14} /> New product</button>
      </div>

      {selected.length > 0 && (
        <div className="glass mb-4 flex flex-wrap items-center gap-3 rounded-2xl p-4" data-testid="bulk-actions-bar">
          <span className="text-xs uppercase tracking-widest text-muted-foreground">{selected.length} selected</span>
          <input data-testid="bulk-price-input" type="number" min="0" value={bulkPrice} onChange={(e) => setBulkPrice(e.target.value)} placeholder="New price ₹"
            className="w-32 rounded-xl border border-input bg-white/80 px-3 py-2 text-xs outline-none" />
          <button data-testid="bulk-price-apply" onClick={() => bulkPrice && bulkApply({ price: Number(bulkPrice) })} className={btnGhost}>Set price</button>
          <button data-testid="bulk-publish" onClick={() => bulkApply({ is_active: true })} className={btnGhost}>Publish</button>
          <button data-testid="bulk-unpublish" onClick={() => bulkApply({ is_active: false })} className={btnGhost}>Unpublish</button>
          <button data-testid="bulk-clear" onClick={() => setSelected([])} className="text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground">Clear</button>
        </div>
      )}

      {editing && (
        <form onSubmit={save} className="glass-deep mb-6 rounded-3xl p-6" data-testid="product-form">
          <div className="flex items-center justify-between">
            <p className="font-display text-lg">{editing === "new" ? "New product" : `Edit: ${editing.name}`}</p>
            <button type="button" onClick={() => setEditing(null)} data-testid="product-form-close" aria-label="Close" className="rounded-full p-1.5 hover:bg-secondary"><X size={16} /></button>
          </div>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <label className={labelCls}>Name<input data-testid="product-name-input" required className={inputCls} value={form.name} onChange={set("name")} /></label>
            <label className={labelCls}>Category / subcategory
              <select data-testid="product-category-select" className={inputCls} value={form.category_id} onChange={set("category_id")}>
                <option value="">Uncategorised</option>
                {categories.filter((c) => !c.parent_id).map((c) => (
                  <optgroup key={c.id} label={c.name}>
                    <option value={c.id}>{c.name}</option>
                    {categories.filter((s) => s.parent_id === c.id).map((s) => <option key={s.id} value={s.id}>— {s.name}</option>)}
                  </optgroup>
                ))}
              </select>
            </label>
            <label className={labelCls}>Price (₹)<input data-testid="product-price-input" required type="number" min="0" className={inputCls} value={form.price} onChange={set("price")} /></label>
            <label className={labelCls}>Compare-at price (₹)<input data-testid="product-compare-input" type="number" min="0" className={inputCls} value={form.compare_at_price} onChange={set("compare_at_price")} /></label>
            <label className={labelCls}>Stock<input data-testid="product-stock-input" type="number" min="0" className={inputCls} value={form.stock} onChange={set("stock")} /></label>
            <div className="flex flex-wrap items-end gap-4 pb-1">
              {[["is_new", "New"], ["is_bestseller", "Bestseller"], ["is_trending", "Trending"]].map(([k, l]) => (
                <label key={k} className="flex items-center gap-2 text-xs uppercase tracking-widest">
                  <input type="checkbox" data-testid={`product-flag-${k}`} checked={!!form[k]} onChange={set(k)} className="accent-primary" /> {l}
                </label>
              ))}
              <label className="flex items-center gap-2 text-xs uppercase tracking-widest">
                <input type="checkbox" data-testid="product-flag-is_active" checked={!!form.is_active} onChange={set("is_active")} className="accent-primary" /> Published (visible)
              </label>
            </div>
            <label className={`${labelCls} sm:col-span-2`}>Description<textarea data-testid="product-description-input" rows={3} className={inputCls} value={form.description} onChange={set("description")} /></label>
            <label className={`${labelCls} sm:col-span-2`}>Highlights — one per line (shown as bullet points)
              <textarea data-testid="product-highlights-input" rows={2} className={inputCls} value={form.highlights_text || ""} onChange={set("highlights_text")} placeholder={"Hand-picked piece\nGift wrapping included"} /></label>
            <label className={labelCls}>Tags — comma separated
              <input data-testid="product-tags-input" className={inputCls} value={form.tags_text || ""} onChange={set("tags_text")} placeholder="gold, watch, gift" /></label>
            <label className={labelCls}>Simple options — comma separated letters (e.g. ring designs A, B, C)
              <input data-testid="product-variants-input" className={inputCls} value={form.variants_text || ""} onChange={set("variants_text")} placeholder="A, B, C, D" /></label>
            <VariantEditor groups={form.variant_groups || []} onChange={(g) => setForm((f) => ({ ...f, variant_groups: g }))} />
          </div>
          <div className="mt-4">
            <ImageManager
              productId={editing === "new" ? null : editing.id}
              images={editing === "new" ? [] : editing.images || []}
              staged={stagedImages}
              onStaged={setStagedImages}
              onServer={(imgs) => { setEditing((prev) => ({ ...prev, images: imgs })); load(); }}
            />
          </div>
          <div className="mt-5 flex flex-wrap gap-3">
            <button type="submit" data-testid="product-save-btn" className={btnPrimary}>{form.is_active ? "Save & publish" : "Save as draft"}</button>
            <button type="button" onClick={() => setForm((f) => ({ ...f, is_active: !f.is_active }))} data-testid="product-draft-toggle"
              className="rounded-full border border-input px-5 py-2.5 text-xs tracking-[0.15em] uppercase hover:bg-secondary">
              {form.is_active ? "Switch to draft" : "Switch to published"}
            </button>
          </div>
        </form>
      )}

      <div className="glass overflow-x-auto rounded-2xl">
        <table className="w-full min-w-[640px] text-left text-sm" data-testid="products-table">
          <thead><tr className="border-b border-input text-xs uppercase tracking-widest text-muted-foreground">
            <th className="p-4"><input type="checkbox" data-testid="select-all-products" checked={allVisibleSelected}
              onChange={() => setSelected(allVisibleSelected ? [] : filtered.map((p) => p.id))} className="accent-primary" /></th>
            <th className="p-4">Product</th><th className="p-4">Price</th><th className="p-4">Stock</th><th className="p-4">Status</th><th className="p-4 text-right">Actions</th>
          </tr></thead>
          <tbody>
            {filtered.map((p) => (
              <tr key={p.id} className="border-b border-input/50 last:border-0" data-testid={`admin-product-row-${p.id}`}>
                <td className="p-4"><input type="checkbox" data-testid={`product-select-${p.id}`} checked={selected.includes(p.id)} onChange={() => toggleSelect(p.id)} className="accent-primary" /></td>
                <td className="p-4"><div className="flex items-center gap-3">
                  {p.images?.[0] ? <img src={imgUrl(p.images[0])} alt="" className="h-11 w-9 rounded-lg object-cover" /> : <span className="flex h-11 w-9 items-center justify-center rounded-lg bg-secondary font-display">A</span>}
                  <span className="line-clamp-1 font-light">{p.name}</span>
                </div></td>
                <td className="p-4">{formatINR(p.price)}</td>
                <td className="p-4">{p.stock}</td>
                <td className="p-4 text-[10px] uppercase tracking-widest text-muted-foreground">
                  {[p.is_active ? "live" : "draft", p.is_new && "new", p.is_bestseller && "best", p.is_trending && "trend"].filter(Boolean).join(" · ")}
                </td>
                <td className="p-4"><div className="flex justify-end gap-1">
                  <button data-testid={`product-duplicate-${p.id}`} onClick={() => duplicate(p.id)} aria-label="Duplicate" className="rounded-full p-2 hover:bg-secondary"><Copy size={14} /></button>
                  <button data-testid={`product-edit-${p.id}`} onClick={() => openEdit(p)} aria-label="Edit" className="rounded-full p-2 hover:bg-secondary"><Pencil size={14} /></button>
                  <button data-testid={`product-delete-${p.id}`} onClick={() => remove(p.id)} aria-label="Delete" className="rounded-full p-2 hover:bg-secondary hover:text-destructive"><Trash2 size={14} /></button>
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
        {loaded && filtered.length === 0 && <p className="p-8 text-center text-sm font-light text-muted-foreground" data-testid="products-empty">No products match your filters.</p>}
      </div>
    </div>
  );
}
